class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = theme.utils.debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.onInputChange.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    // Add event listener for price range apply button
    this.addEventListener('click', this.onPriceRangeApply.bind(this));

    // Add event listener for drawer overlay click to close drawer
    this.setupDrawerOverlayClose();
    this.setupDrawerAccessibility();
  }

  static setListeners() {
    FacetFiltersForm.committedFilterHTML = document.querySelector('.facets-container')?.outerHTML || '';
    const onHistoryChange = () => {
      FacetFiltersForm.cancelPendingSubmissions();
      const searchParams = window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev && !FacetFiltersForm.renderAbortController) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    FacetFiltersForm.renderProductPerPage();
    window.addEventListener('popstate', onHistoryChange);
    document.addEventListener('productsLoaded', () => {
      if (FacetFiltersForm.renderAbortController) return;
      FacetFiltersForm.committedSearchParams = window.location.search.slice(1);
      FacetFiltersForm.searchParamsPrev = FacetFiltersForm.committedSearchParams;
    });
  }

  disconnectedCallback() {
    this.debouncedOnSubmit.cancel();
    this.cleanupDrawerAccessibility?.();
  }

  static cancelPendingSubmissions() {
    document.querySelectorAll('facet-filters-form').forEach((form) => {
      form.debouncedOnSubmit?.cancel();
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    searchParams = new URLSearchParams(searchParams || '').toString();
    searchParams = window.lcFacetGroups?.normalizeSearchParams?.(searchParams) ?? searchParams;
    FacetFiltersForm.cancelPendingSubmissions();
    FacetFiltersForm.pendingUpdateURLHash = updateURLHash;
    if (FacetFiltersForm.pendingSearchParams === searchParams && FacetFiltersForm.renderAbortController &&
        !FacetFiltersForm.renderAbortController.signal.aborted) {
      return FacetFiltersForm.renderPromise;
    }

    const requestId = ++FacetFiltersForm.renderRequestId;
    FacetFiltersForm.renderAbortController?.abort();
    const controller = new AbortController();
    FacetFiltersForm.renderAbortController = controller;
    FacetFiltersForm.pendingSearchParams = searchParams;
    FacetFiltersForm.clearError();
    FacetFiltersForm.setLoading(true);
    const sections = FacetFiltersForm.getSections();

    const requests = sections.map((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      return FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, requestId)
        : FacetFiltersForm.renderSectionFromFetch(url, event, requestId, controller.signal);
    });

    FacetFiltersForm.renderPromise = Promise.all(requests)
      .then((responses) => {
        if (requestId !== FacetFiltersForm.renderRequestId || controller.signal.aborted) return;
        responses.forEach((html) => {
          if (!new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer')) {
            throw new Error('The filter response does not contain a product grid');
          }
        });
        if (FacetFiltersForm.pendingUpdateURLHash) FacetFiltersForm.updateURLHash(searchParams);
        else FacetFiltersForm.replaceURL(searchParams);
        responses.forEach((html) => {
          FacetFiltersForm.renderFilters(html, event);
          FacetFiltersForm.renderProductGridContainer(html);
          FacetFiltersForm.renderProductCount(html);
          FacetFiltersForm.renderProductPerPage();
          FacetFiltersForm.clickGridView();
          FacetFiltersForm.committedFilterHTML = html;
        });
        FacetFiltersForm.committedSearchParams = searchParams;
        FacetFiltersForm.searchParamsPrev = searchParams;
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
        return true;
      })
      .catch((error) => {
        if (error.name === 'AbortError' || controller.signal.aborted || requestId !== FacetFiltersForm.renderRequestId) return;
        console.error('Error loading filters:', error);
        FacetFiltersForm.replaceURL(FacetFiltersForm.committedSearchParams);
        FacetFiltersForm.searchParamsPrev = FacetFiltersForm.committedSearchParams;
        const focusedId = document.activeElement?.id;
        if (FacetFiltersForm.committedFilterHTML) FacetFiltersForm.renderFilters(FacetFiltersForm.committedFilterHTML);
        if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
        FacetFiltersForm.showError(searchParams);
        return false;
      })
      .finally(() => {
        if (requestId !== FacetFiltersForm.renderRequestId) return;
        FacetFiltersForm.setLoading(false);
        FacetFiltersForm.renderAbortController = null;
        FacetFiltersForm.pendingSearchParams = null;
      });
    return FacetFiltersForm.renderPromise;
  }

  static setLoading(loading) {
    document.querySelectorAll('#ProductGridContainer infinite-scrolling').forEach((element) => {
      element.isFiltering = loading;
      if (loading) element.cancelPendingLoad?.();
    });
    document.querySelectorAll('.product-count .loading__spinner, .product-grid-container .loading__spinner')
      .forEach((spinner) => spinner.classList.toggle('hidden', !loading));
    document.querySelectorAll('#ProductGridContainer .collection, #ProductCount, #ProductCountMobile')
      .forEach((element) => element.classList.toggle('loading', loading));
    document.getElementById('ProductGridContainer')?.setAttribute('aria-busy', String(loading));
    if (!loading) FacetFiltersForm.toggleActiveFacets(false);
  }

  static replaceURL(searchParams) {
    const url = new URL(window.location.href);
    url.search = searchParams;
    history.replaceState({ ...history.state, searchParams }, '', `${url.pathname}${url.search}${url.hash}`);
  }

  static clearError() {
    document.querySelectorAll('[data-lc-facet-error]').forEach((element) => element.remove());
  }

  static showError(searchParams) {
    const template = document.querySelector('template[data-lc-facet-error-template]');
    if (!template) return;
    const placements = [
      document.getElementById('ProductGridContainer'),
      document.querySelector('#FacetFiltersFormMobile .mobile-facets__main'),
    ].filter(Boolean);
    placements.forEach((container) => {
      const message = template.content.firstElementChild.cloneNode(true);
      message.querySelector('button').addEventListener('click', (event) => {
        event.preventDefault();
        FacetFiltersForm.renderPage(searchParams);
      });
      container.prepend(message);
    });
  }

  static renderSectionFromFetch(url, event, requestId, signal) {
    return fetch(url, { signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Unable to load filters (${response.status})`);
        return response.text();
      })
      .then((responseText) => {
        if (requestId !== FacetFiltersForm.renderRequestId || signal.aborted) return;
        if (!new DOMParser().parseFromString(responseText, 'text/html').getElementById('ProductGridContainer')) {
          throw new Error('The filter response does not contain a product grid');
        }
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html: responseText, url }];
        return responseText;
      });
  }

  static renderSectionFromCache(filterDataUrl, event, requestId) {
    if (requestId !== FacetFiltersForm.renderRequestId) return;
    return FacetFiltersForm.filterData.find(filterDataUrl).html;
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById('ProductGridContainer');
    const focusedChoice = container.contains(document.activeElement)
      ? document.activeElement.closest('[data-lc-quick-choice]')
      : null;
    const focusedKey = focusedChoice?.dataset.lcQuickChoice;
    const focusedGroup = focusedChoice?.closest('.lc-quickfilters__row')?.getAttribute('aria-label');
    container.innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    if (focusedKey) {
      const choices = Array.from(container.querySelectorAll('[data-lc-quick-choice]'));
      const replacement = choices.find((choice) => choice.dataset.lcQuickChoice === focusedKey) ||
        choices.find((choice) => focusedGroup &&
          choice.closest('.lc-quickfilters__row')?.getAttribute('aria-label') === focusedGroup) || choices[0];
      replacement?.focus({ preventScroll: true });
    }

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    // Rebind 'show more swatches' buttons after grid re-render
    if (typeof initMoreSwatchButtons === 'function') {
      const container = document.getElementById('ProductGridContainer');
      initMoreSwatchButtons(container || document);
    }
  }

  static clickGridView (event) {
    document.querySelectorAll('.button--grid-view').forEach((button) => {
      if (FacetFiltersForm.boundGridViewButtons.has(button)) return;
      FacetFiltersForm.boundGridViewButtons.add(button);
      button.addEventListener('click', this.changeLayoutGrid.bind(this));
    });
  }

  static changeLayoutGrid (event) {
    const gridView = event.currentTarget.dataset.grid;
    document.querySelectorAll('.button--grid-view').forEach((button) => {
      button.classList.add('cursor-pointer');
      button.classList.remove('active');
    });

    event.currentTarget.classList.remove('cursor-pointer');
    event.currentTarget.classList.add('active');
    document.querySelector('.product-grid-container .product-grid').setAttribute('data-view', gridView);
  }

  static renderProductPerPage() {
    const productGrid = document.querySelector('.product-grid-container .product-grid');

    document.querySelectorAll('input[name="per_page"]').forEach(function(input) {
      if (FacetFiltersForm.boundPerPageInputs.has(input)) return;
      FacetFiltersForm.boundPerPageInputs.add(input);
      input.addEventListener('change', function() {
        FacetFiltersForm.renderAbortController?.abort();
        const detailsList = input.closest('.details__list');
        const currentSelected = detailsList ? detailsList.querySelector('.selected') : null;
        if (currentSelected) currentSelected.classList.remove('selected', 'pointer-events-none');

        const currentItem = input.closest('.facet-filters__sort_item');
        if (currentItem) currentItem.classList.add('selected', 'pointer-events-none');

        const labelText = input.closest('details') ? input.closest('details').querySelector('.label-text') : null;
        if (labelText) labelText.innerHTML = this.value;

        if (detailsList) {
          detailsList.querySelectorAll('input[name="per_page"]').forEach((other) => {
            if (other !== input) other.checked = false;
          });
          detailsList.querySelectorAll('.facet-filters__sort_item').forEach((li) => {
            if (li !== currentItem) li.classList.remove('selected', 'pointer-events-none');
          });
        }

        var data = new URLSearchParams();
        data.append('attributes[pagination]', this.value);

        const requestId = ++FacetFiltersForm.perPageRequestId;
        FacetFiltersForm.paginationUpdateQueue = FacetFiltersForm.paginationUpdateQueue
          .catch(() => {})
          .then(() => fetch('/cart/update.js', {
            method: 'POST',
            body: data,
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          }))
          .then((response) => {
            if (!response.ok) throw new Error(`Unable to update page size (${response.status})`);
            return response.text();
          })
          .then(() => {
            if (requestId !== FacetFiltersForm.perPageRequestId) return;
            FacetFiltersForm.filterData = [];
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.delete('page');
            searchParams.delete('per_page');
            FacetFiltersForm.renderAbortController?.abort();
            return FacetFiltersForm.renderPage(searchParams.toString());
          })
          .catch((error) => console.error('Error updating page size:', error));
      });
    });

    if (productGrid) {
      const gridViewButton = document.querySelector('.button--grid-view.active');

      if (gridViewButton) {
        productGrid.setAttribute('data-view', gridViewButton.dataset.grid);
      }
    }
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const count = parsedHTML.getElementById('ProductCount') || parsedHTML.getElementById('ProductCountMobile');
    ['ProductCount', 'ProductCountMobile'].forEach((id) => {
      const container = document.getElementById(id);
      if (!container) return;
      if (count) container.innerHTML = count.innerHTML;
      container.classList.remove('loading');
    });

    const loadingSpinners = document.querySelectorAll(
      '.product-count .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    let focusDetailsId;
    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        focusDetailsId = closestJSFilterID;
      }
    }
    window.lcFacetGroups?.refresh?.();
    document.querySelectorAll('facet-filters-form').forEach((form) => {
      if (!form.pendingPriceValues) return;
      Object.entries(form.pendingPriceValues).forEach(([name, value]) => {
        const input = form.querySelector(`input[name="${name}"]`);
        if (input) input.value = value;
      });
      form.querySelectorAll('price-range').forEach((range) => range.syncValues?.(null, false));
    });
    if (focusDetailsId) FacetFiltersForm.restoreFacetFocus(event, document.getElementById(focusDetailsId));
  }

  static restoreFacetFocus(event, details) {
    if (!details) return;
    const replacement = event.target.id ? document.getElementById(event.target.id) : null;
    const target = replacement && !replacement.disabled && details.contains(replacement)
      ? replacement
      : details.querySelector('.facets__summary, .mobile-facets__summary, summary');
    target?.focus({ preventScroll: true });
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();

    const facetFiltersForm = document.querySelector('facet-filters-form');
    if (facetFiltersForm) {
      facetFiltersForm.setupDrawerOverlayClose();
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    if (!target) return;
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const url = `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`;
    const state = { ...history.state, searchParams };
    if (`${window.location.pathname}${window.location.search}` === url) {
      history.replaceState(state, '', `${url}${window.location.hash}`);
    } else {
      history.pushState(state, '', `${url}${window.location.hash}`);
    }
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    form.querySelectorAll('price-range').forEach((range) => range.syncValues?.(null, true));
    const formData = new FormData(form);
    formData.delete('page');
    formData.delete('per_page');
    const minInput = form.querySelector('input[name="filter.v.price.gte"]');
    const maxInput = form.querySelector('input[name="filter.v.price.lte"]');
    const minRangeHost = minInput ? minInput.closest('price-range') : null;
    const maxRangeHost = maxInput ? maxInput.closest('price-range') : null;
    const minRange = minRangeHost ? minRangeHost.querySelector('.filter__price_min') : null;
    const maxRange = maxRangeHost ? maxRangeHost.querySelector('.filter__price_max') : null;

    const isUnchangedBound = (input, candidates) => {
      if (!input) return false;
      const value = input.value.trim();
      if (!value) return true;
      const amount = PriceRange.parseAmount(value);
      return candidates.some((candidate) => Number.isFinite(amount) &&
        candidate !== undefined && candidate !== null && PriceRange.parseAmount(candidate) === amount);
    };

    // Een volledig prijsbereik beperkt niets en hoort daarom niet in de URL of actieve chips.
    if (
      isUnchangedBound(minInput, [
        minInput && minInput.dataset.min,
        minRange && minRange.min,
      ])
    ) {
      formData.delete(minInput.name);
    }
    if (
      isUnchangedBound(maxInput, [
        maxInput && maxInput.dataset.max,
        maxInput && maxInput.max,
        maxRange && maxRange.max,
      ])
    ) {
      formData.delete(maxInput.name);
    }

    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    const params = new URLSearchParams(searchParams);
    params.delete('page');
    return FacetFiltersForm.renderPage(params.toString(), event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const currentForm = event.target.closest('form');
    if (!currentForm) return;
    const allForms = Array.from(document.querySelectorAll('facet-filters-form form'));
    const isMobile = currentForm.id === 'FacetFiltersFormMobile';
    const filterForm = allForms.find((form) => form.id === 'FacetFiltersForm') ||
      allForms.find((form) => form.id === 'FacetFiltersFormMobile');
    const forms = isMobile ? [] : allForms.filter((form) =>
      form === filterForm || form.id === 'FacetSortForm' || form.id === 'FacetSortDrawerForm');
    const params = new URLSearchParams();
    [...forms.filter((form) => form !== currentForm), currentForm].forEach((form) => {
      new URLSearchParams(this.createSearchParams(form)).forEach((value, name) => {
        if (name.startsWith('filter.')) {
          if (!params.getAll(name).includes(value)) params.append(name, value);
        } else {
          params.set(name, value);
        }
      });
      const owner = form.closest('facet-filters-form');
      if (owner) owner.pendingPriceValues = null;
    });
    return this.onSubmitForm(params.toString(), event);
  }

  onInputChange(event) {
    // Check if the input is a price range input
    const isPriceRangeInput = event.target.classList.contains('filter__price_change') ||
                             event.target.classList.contains('filter__price_number');

    if (isPriceRangeInput) {
      this.pendingPriceValues = Object.fromEntries(Array.from(this.querySelectorAll('.filter__price_number'))
        .map((input) => [input.name, input.value]));
    }

    // Only auto-submit if it's not a price range input
    if (!isPriceRangeInput && event.target.name !== 'per_page') {
      FacetFiltersForm.renderAbortController?.abort();
      this.debouncedOnSubmit(event);
    }
  }

  onPriceRangeApply(event) {
    // Check if the clicked element is the price range apply button
    if (event.target.closest('.facets-price__apply button')) {
      event.preventDefault();

      // Find the form and submit it manually
      const form = this.querySelector('form');
      if (form) {
        this.onSubmitHandler(event);
      }
    }
  }

  applyAndCloseDrawer(event) {
    const form = event.target.closest('form');
    if (!form || form.id !== 'FacetFiltersFormMobile') return;
    form.querySelectorAll('price-range').forEach((range) => range.syncValues?.(null, true));
    if (!form.reportValidity()) return;
    this.onSubmitHandler(event);
    this.closeDrawer(event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = new URL(event.currentTarget.href, window.location.href);
    url.searchParams.delete('page');
    FacetFiltersForm.renderPage(url.searchParams.toString());

    if(window.innerWidth <= 1024) {
      document.querySelectorAll('.mobile-facets__details').forEach(item => {
        item.removeAttribute('style');
        item.removeAttribute('open')
      })
    }
  }

  setupDrawerOverlayClose() {
    const drawerOverlay = this.querySelector('[data-drawer-overlay]');
    if (drawerOverlay && this.boundDrawerOverlay !== drawerOverlay) {
      this.boundDrawerOverlay = drawerOverlay;
      drawerOverlay.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeDrawer(event);
      });
    }
  }

  setupDrawerAccessibility() {
    const details = this.closest('details.mobile-facets__disclosure');
    const panel = this.querySelector('.mobile-facets__inner');
    if (!details || !panel) return;
    this.mobileDetails = details;
    this.mobilePanel = panel;
    this.mobileTrigger = details.querySelector(':scope > summary');
    this.mobileBreakpoint = window.matchMedia('(max-width: 1024px)');
    this.inertBackground = [];
    const onToggle = () => {
      if (details.open && this.mobileBreakpoint.matches) this.activateDrawerFocus();
      else this.releaseDrawerFocus(false);
    };
    const onTrigger = (event) => {
      if (this.drawerFocusActive && this.mobileTrigger.contains(event.target)) this.releaseDrawerFocus(false);
    };
    const onResize = () => {
      if (!this.mobileBreakpoint.matches && details.open) this.closeDrawer();
    };
    this.onDrawerKeydown = (event) => {
      if (!this.drawerFocusActive) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.closeDrawer(event);
      } else if (event.key === 'Tab') {
        const focusable = this.getDrawerFocusableElements();
        const first = focusable[0] || panel;
        const last = focusable[focusable.length - 1] || panel;
        if (!panel.contains(document.activeElement) ||
            (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) ||
            (!event.shiftKey && document.activeElement === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus({ preventScroll: true });
        }
      }
    };
    this.onDrawerFocusin = (event) => {
      if (this.drawerFocusActive && !panel.contains(event.target)) {
        (this.getDrawerFocusableElements()[0] || panel).focus({ preventScroll: true });
      }
    };
    details.addEventListener('toggle', onToggle);
    details.addEventListener('click', onTrigger, true);
    this.mobileBreakpoint.addEventListener('change', onResize);
    this.cleanupDrawerAccessibility = () => {
      this.releaseDrawerFocus(false);
      details.removeEventListener('toggle', onToggle);
      details.removeEventListener('click', onTrigger, true);
      this.mobileBreakpoint.removeEventListener('change', onResize);
    };
    if (details.open) onToggle();
  }

  getDrawerFocusableElements() {
    return Array.from(this.mobilePanel.querySelectorAll('a[href], button, input, select, textarea, summary, [tabindex]'))
      .filter((element) => element.tabIndex >= 0 && !element.matches(':disabled') &&
        !element.closest('[inert]') && element.getClientRects().length &&
        getComputedStyle(element).visibility !== 'hidden');
  }

  activateDrawerFocus() {
    if (this.drawerFocusActive) return;
    this.drawerFocusActive = true;
    removeTrapFocus();
    (this.mobilePanel.querySelector('.drawer__close') || this.mobilePanel).focus({ preventScroll: true });
    let branch = this.mobilePanel;
    while (branch.parentElement && branch !== document.body) {
      for (const sibling of branch.parentElement.children) {
        if (sibling === branch || !(sibling instanceof HTMLElement) || sibling.matches('[data-drawer-overlay]') || /^(SCRIPT|STYLE|LINK)$/.test(sibling.tagName)) continue;
        this.inertBackground.push([sibling, sibling.inert]);
        sibling.inert = true;
      }
      branch = branch.parentElement;
    }
    document.addEventListener('keydown', this.onDrawerKeydown, true);
    document.addEventListener('focusin', this.onDrawerFocusin, true);
  }

  releaseDrawerFocus(restoreFocus = true) {
    if (!this.drawerFocusActive) return;
    this.drawerFocusActive = false;
    document.removeEventListener('keydown', this.onDrawerKeydown, true);
    document.removeEventListener('focusin', this.onDrawerFocusin, true);
    this.inertBackground.forEach(([element, wasInert]) => { element.inert = wasInert; });
    this.inertBackground = [];
    if (restoreFocus && this.mobileTrigger?.isConnected) this.mobileTrigger.focus({ preventScroll: true });
  }

  closeDrawer(event) {
    event?.preventDefault();
    event?.stopImmediatePropagation();
    const menuDrawer = this.closest('menu-drawer');
    const details = this.mobileDetails || menuDrawer?.querySelector('details');
    const trigger = this.mobileTrigger || details?.querySelector(':scope > summary');
    this.releaseDrawerFocus(false);
    if (menuDrawer?.closeMenuDrawer && details?.open) {
      menuDrawer.closeMenuDrawer(event || new Event('close'), trigger);
    } else if (details) {
      details.open = false;
      trigger?.focus({ preventScroll: true });
    }
    trigger?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden-mobile');
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.renderRequestId = 0;
FacetFiltersForm.renderAbortController = null;
FacetFiltersForm.pendingSearchParams = null;
FacetFiltersForm.pendingUpdateURLHash = true;
FacetFiltersForm.committedSearchParams = window.location.search.slice(1);
FacetFiltersForm.committedFilterHTML = '';
FacetFiltersForm.renderPromise = null;
FacetFiltersForm.boundPerPageInputs = new WeakSet();
FacetFiltersForm.boundGridViewButtons = new WeakSet();
FacetFiltersForm.perPageRequestId = 0;
FacetFiltersForm.paginationUpdateQueue = Promise.resolve();
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
if (!customElements.get('facet-filters-form')) customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.rangeInputs = Array.from(this.querySelectorAll('.filter__price_change'));
    this.numberInputs = Array.from(this.querySelectorAll('.filter__price_number'));
    if (this.rangeInputs.length !== 2 || this.numberInputs.length !== 2) return;
    this.minimum = PriceRange.parseAmount(this.rangeInputs[0].min) || 0;
    this.maximum = PriceRange.parseAmount(this.numberInputs[1].dataset.max);
    if (!Number.isFinite(this.maximum)) this.maximum = PriceRange.parseAmount(this.rangeInputs[1].max);
    this.maximum = Math.max(this.minimum, this.maximum || 0);
    this.rangeInputs.forEach((input) => {
      input.step = '0.01';
      input.min = this.minimum;
      input.max = this.maximum;
      input.addEventListener('input', () => {
        const index = this.rangeInputs.indexOf(input);
        this.numberInputs[index].value = input.value;
        this.syncValues(this.numberInputs[index], true);
      });
    });
    this.numberInputs.forEach((input) => {
      input.addEventListener('input', () => this.syncValues(input, false));
      input.addEventListener('change', () => this.syncValues(input, true));
      input.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.syncValues(null, true);
  }

  static parseAmount(value) {
    let normalized = String(value ?? '').trim().replace(/[\s'’]/g, '');
    if (!normalized) return NaN;
    if (normalized.includes(',') && normalized.includes('.')) {
      const decimal = normalized.lastIndexOf(',') > normalized.lastIndexOf('.') ? ',' : '.';
      normalized = normalized.replace(decimal === ',' ? /\./g : /,/g, '').replace(',', '.');
    } else {
      normalized = normalized.replace(',', '.');
    }
    return Number(normalized);
  }

  onKeyDown(event) {
    if (event.metaKey || event.ctrlKey) return;
    const pattern = /^(?:[0-9.,' ]|Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape|Home|End)$/;
    if (!pattern.test(event.key)) event.preventDefault();
  }

  syncValues(changedInput, commit = true) {
    const values = this.numberInputs.map((input, index) => {
      const value = PriceRange.parseAmount(input.value);
      const fallback = index === 0 ? this.minimum : this.maximum;
      return Math.min(this.maximum, Math.max(this.minimum, Number.isFinite(value) ? value : fallback));
    });
    if (values[0] > values[1]) {
      if (changedInput === this.numberInputs[1]) values[1] = values[0];
      else values[0] = values[1];
    }
    this.rangeInputs.forEach((input, index) => {
      input.value = values[index];
      if (commit && this.numberInputs[index].value.trim() !== '') {
        this.numberInputs[index].value = String(values[index]);
      }
    });
    this.updateDisplay(values[0], values[1]);
  }

  updateDisplay(value1, value2) {
    const priceRange = this.querySelector('.facets-price__range');
    if (!priceRange) return;
    const span = this.maximum - this.minimum;
    const left = span > 0 ? (value1 - this.minimum) / span * 100 : 0;
    const right = span > 0 ? (this.maximum - value2) / span * 100 : 0;
    priceRange.style.setProperty('--left-space', left + '%');
    priceRange.style.setProperty('--right-space', right + '%');
  }
}
if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    if (facetLink.hasAttribute('data-lc-quick-choice')) {
      facetLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') event.preventDefault();
      });
    }
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}
if (!customElements.get('facet-remove')) customElements.define('facet-remove', FacetRemove);
